import { isValidAsin } from "../../util/stringUtils";

/**
 * Exact-match (`=`) ASIN chips whose value isn't a valid ASIN stay on
 * screen, shown in red, so the user can see and fix the typo. They are
 * left out of the query sent to Redux / the URL / the backend.
 * Contains (`:`) chips, which only legacy links produce, are partial
 * by design and never flagged.
 *
 * Both helpers take the Polaris query shape (`tokens` / `tokenGroups`,
 * nested groups carry `operation`) and are pure.
 */
const isInvalidAsinToken = (token: any): boolean =>
  token?.propertyKey === "asin" &&
  token?.operator === "=" &&
  typeof token?.value === "string" &&
  token.value !== "" &&
  !isValidAsin(token.value);

/** Values of invalid exact ASIN chips, deduplicated, in display order. */
export function findInvalidAsins(query: any): string[] {
  const found = new Set<string>();
  const walk = (items: any[] = []) =>
    items.forEach((item) => {
      if (item?.operation) {
        walk(item.tokens);
        walk(item.tokenGroups);
      } else if (isInvalidAsinToken(item)) {
        found.add(item.value);
      }
    });
  walk(query?.tokens);
  walk(query?.tokenGroups);
  return Array.from(found);
}

/**
 * Copy of `query` without invalid exact ASIN chips. Nested groups left
 * empty are removed; the root is always kept.
 */
export function dropInvalidAsinTokens(query: any): any {
  const prune = (items: any[] = []): any[] =>
    items.reduce((kept: any[], item: any) => {
      if (item?.operation) {
        const tokens = prune(item.tokens);
        const tokenGroups = prune(item.tokenGroups);
        if (tokens.length + tokenGroups.length > 0) {
          kept.push({ ...item, tokens, tokenGroups });
        }
      } else if (!isInvalidAsinToken(item)) {
        kept.push(item);
      }
      return kept;
    }, []);
  return {
    ...query,
    tokens: prune(query?.tokens),
    tokenGroups: prune(query?.tokenGroups),
  };
}
